// Safety Check-In client helpers: trusted contacts, check-in/out/emergency
// alerts (SMS via the sendSafetySms Cloud Function), and check-in history.
import { getAuth } from "firebase/auth";
import {
  doc,
  updateDoc,
  addDoc,
  collection,
  serverTimestamp,
  query,
  orderBy,
  limit,
  getDocs,
} from "firebase/firestore";
import { db } from "../Configuration/firebase";

const FUNCTIONS_BASE =
  "https://us-central1-one-address-53a06.cloudfunctions.net";

export const MAX_TRUSTED_CONTACTS = 10;

// Persists the full trusted-contacts array on the user doc. Contacts are a
// small bounded list, so an array field (covered by the existing owner-only
// user-doc rule) is simpler than a subcollection.
export async function saveTrustedContacts(uid, contacts) {
  await updateDoc(doc(db, "users", uid), { trustedContacts: contacts });
}

// Sends a safety SMS to all trusted contacts via the backend (which reads the
// contacts server-side and applies Twilio). `type` is checkin | checkout |
// emergency. Returns { sent } - the number of contacts texted.
export async function sendSafetyAlert({ type, addressTitle, address }) {
  const user = getAuth().currentUser;
  if (!user) throw new Error("You must be signed in.");
  const token = await user.getIdToken();
  const resp = await fetch(`${FUNCTIONS_BASE}/sendSafetySms`, {
    method: "POST",
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify({ type, addressTitle, address }),
  });
  const text = await resp.text();
  let data = {};
  try {
    data = text ? JSON.parse(text) : {};
  } catch {
    data = { message: text };
  }
  if (!resp.ok) {
    throw new Error(data.message || "Couldn't send the alert.");
  }
  return data;
}

// Appends a record to the user's check-in history subcollection.
export async function recordCheckinHistory(uid, entry) {
  await addDoc(collection(db, "users", uid, "checkins"), {
    ...entry,
    createdAt: serverTimestamp(),
  });
}

// Sets (or clears, with null) the user's single active check-in.
export async function setActiveCheckin(uid, activeCheckin) {
  await updateDoc(doc(db, "users", uid), { activeCheckin });
}

// Loads recent check-in history, newest first.
export async function fetchCheckinHistory(uid, max = 25) {
  const q = query(
    collection(db, "users", uid, "checkins"),
    orderBy("createdAt", "desc"),
    limit(max),
  );
  const snap = await getDocs(q);
  return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
}
